import React, { useContext } from 'react'
import { useInView } from 'react-intersection-observer'

import { GeolocalizationContext } from '../../utils/contexts/geolocalization-context'
import { localizePath } from '../../utils/helpers/localization/localize'
import Image from '../parts/image'
import Link from '../parts/link'
import Heading from '../parts/heading'
import SocialLinks from '../elements/social-links'
import Stores from '../elements/store/store-buttons'
import FooterNav from '../elements/nav/nav-footer'
import Ratings from '../elements/ratings'
import Copyrights from '../elements/copyrights'

const Footer = ({ pageContext, data, theme, customThemeOptions, customFooterContent: CustomFooterContent }) => {
  if (!data) {
    return ''
  }
  const [refLoad, inViewLoad] = useInView({
    threshold: 0,
    triggerOnce: true
  })
  const { rating } = useContext(GeolocalizationContext)
  const [gameRating, setGameRating] = rating

  const footerNav = data.footerNavData
  const brand = data.brandData
  const footer = data.footerData
  const socialLinks = data.socialData
  const gameData = data.gameData
  const additionnalLogo = footer?.additionnalLogo
  const additionnalLogoLink = footer?.additionnalLogoLink

  const strings = data.stringsData

  return (
    !customThemeOptions.disableFooter && (
      <footer className={`text-base`}>
        <div
          ref={refLoad}
          className="footer-primary relative"
        >
          {brand.alternateLogo && (
            <div className="footer-logo-wrapper container flex flex-col items-center py-24 text-white">
              <Link className="footer-logo-link" to={localizePath({ ...pageContext, slug: '' })}>
                <Image
                  media={brand.alternateLogo ? brand.alternateLogo : brand.fullLogo}
                  className="footer-logo"
                />
              </Link>
            </div>
          )}

          {/* BUY GAME */}
          {footer.buyGameDisplay && footer.buyGameDisplay === true && gameData && gameData.gameLinks !== null && !customThemeOptions.removeFooterStoreLinks && (
            <div className="footer-buy-section px-8 lg:container flex flex-col items-center text-white mb-10">
              {footer.storeTitle && (
                <Heading
                  text={footer.storeTitle}
                  tag={'h3'}
                  className={theme?.storeTitle ? theme.storeTitle : 'mb-6 text-center text-lg font-bold'}
                />
              )}
              {inViewLoad && (
                <Stores
                  pageContext={pageContext}
                  layout="icons"
                  product={footer.storeProduct ? footer.storeProduct : 'base-game'}
                  gameData={gameData}
                  className="p-0 m-0 flex justify-center"
                  projectOrder={footer.buyLinksCustomOrder}
                  eventAction={pageContext?.dataLayer?.storeLinkIcon?.event}
                  eventPage={data?.dataLayer?.trackingPageName}
                  eventSection="footer"
                />
              )}
            </div>
          )}

          {/* SOCIAL */}
          {footer.socialDisplay && footer.socialDisplay === true && socialLinks && socialLinks !== null && !customThemeOptions.removeFooterSocial && (
            <div className="footer-social-section mx-16 lg:container flex flex-col items-center text-white pb-24">
              {footer.socialTitle && (
                <Heading
                  text={footer.socialTitle}
                  tag={'h3'}
                  className={theme?.socialTitle ? theme.socialTitle : 'mb-6 text-center text-lg font-bold'}
                />
              )}
              {inViewLoad && (
                <SocialLinks
                  pageContext={pageContext}
                  social={socialLinks}
                  className="flex gap-x-8"
                  iconClassName="social-links-item"
                  eventSection="footer"
                />
              )}
              {strings?.labels?.logoTagLine && (
                <span className="footer-logo-tagline">
                  {pageContext.locale === 'ja' ? (
                    <>
                      {brand.tagLineLogo && (
                        <Image
                          media={brand.tagLineLogo}
                          className="footer-logo-tagline-icon"
                        />
                      )}
                      {strings?.labels?.logoTagLine}
                    </>
                  ) : (
                    <>
                      {strings?.labels?.logoTagLine}
                      {brand.tagLineLogo && (
                        <Image
                          media={brand.tagLineLogo}
                          className="footer-logo-tagline-icon"
                        />
                      )}
                    </>
                  )}
                </span>
              )}
            </div>
          )}

          {/* RATING */}
          {!footer.disableRatings && (
            <div className="footer-rating-section container flex flex-col items-center border-t-2 py-24 text-white">
              {inViewLoad && (
                <Ratings
                  data={footer}
                  locale={pageContext.locale}
                />
              )}
            </div>
          )}

          {/* DYNAMIC ZONE - For displaying custom content in footer */}
          {CustomFooterContent && (
            <CustomFooterContent />
          )}
        </div>

        {/* FOOTER NAV */}
        <div
          className={`
          footer-secondary relative flex flex-col justify-center py-12
          ${theme?.FooterNav?.bg ? `bg-${theme.FooterNav.bg}` : 'bg-black'}
          ${theme?.FooterNav?.text?.default ? `text-${theme.FooterNav.text.default}` : 'text-white'}
        `}
        >
          <div className="footer-nav lg:container px-9 lg:px-0">
            {footerNav && footerNav !== null && (
              <FooterNav
                links={footerNav}
                logo={additionnalLogo}
                logoLink={additionnalLogoLink}
                theme={theme.FooterNav}
              />
            )}
            {gameRating?.code === 'cero' && (
              <div className="footer-nav-copyrights">
                <div className="text-[11px] tracking-wider text-center uppercase leading-normal">
                  <span className="block">{`"PlayStationmark", "PlayStation", "PlayStation5" and "PlayStation4" are registered trademarks or trademarks of Sony Interactive Entertainment Inc.`}</span>
                  <span>{`Microsoft Xbox Series X, Xbox Series S, Xbox One and related Xbox logos are trademarks and/or registered trademarks of Microsoft Corporation and the Microsoft > group of companies.`}</span>
                </div>
                <Copyrights
                  className="text-[11px] tracking-wider text-center uppercase leading-normal"
                  text='Valve Corporation. Steam and the Steam logo are trademarks and/or registered trademarks of Valve Corporation in the U.S. and/or other countries.'
                />
              </div>
            )}
            {footer?.legalText && (
              <Copyrights
                className="footer-nav-copyrights text-[11px] tracking-wider text-center uppercase leading-normal"
                startYear={footer.startYear}
                text={footer.legalText}
              />
            )}
          </div>
        </div>
      </footer >
    )
  )
}

export default Footer
